import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { rhm, apiError } from "../api";
import { Btn, Field, Input, Select } from "../ui";
import { ACCENT_OPTIONS } from "../../site/accent";
import { blankSection } from "../../site/registry";
import { ImageField, Inspector } from "../pages/websiteFields";
import { AddSection, InlineEditor, SaveTemplateDialog, Shell } from "../pages/websiteDialogs";

const EXTRA_TYPES = ["plain_cards", "product_pairing", "rich_text", "image_banner", "faq",
    "testimonials", "blog_highlight", "consultation_cta", "benefits", "marquee"];

const setPath = (obj, path, value) => {
    const parts = path.split(".");
    const root = Array.isArray(obj) ? [...obj] : { ...(obj || {}) };
    let cur = root;
    for (let i = 0; i < parts.length - 1; i += 1) {
        const key = parts[i];
        const next = cur[key];
        cur[key] = Array.isArray(next) ? [...next] : { ...(next || {}) };
        cur = cur[key];
    }
    cur[parts[parts.length - 1]] = value;
    return root;
};

/**
 * Click-to-edit controller for the product live preview: product text, images, and the
 * product page's own sections. Nothing is written to the database until SAVE.
 */
export const useProductPreviewEdit = ({ productId, draft, setDraft, editable }) => {
    const qc = useQueryClient();
    const [layout, setLayout] = useState(null);
    const [productDirty, setProductDirty] = useState(false);
    const [layoutDirty, setLayoutDirty] = useState(false);
    const [dirtyRoots, setDirtyRoots] = useState([]);
    const [inline, setInline] = useState(null);
    const [images, setImages] = useState(false);
    const [styleFor, setStyleFor] = useState(null);
    const [inspectFor, setInspectFor] = useState(null);
    const [presetFor, setPresetFor] = useState(null);
    const [showAdd, setShowAdd] = useState(false);
    const [saving, setSaving] = useState(false);

    const { data: saved } = useQuery({
        queryKey: ["pl-page", productId],
        queryFn: async () => (await rhm.get(`/site/product-pages/${productId}`)).data,
        enabled: !!productId,
    });
    const { data: template } = useQuery({
        queryKey: ["pl-template"],
        queryFn: async () => (await rhm.get("/site/product-template")).data,
    });
    const { data: products = [] } = useQuery({
        queryKey: ["rhm-products"],
        queryFn: async () => (await rhm.get("/products")).data,
    });

    useEffect(() => { if (saved && layout === null) setLayout(saved); }, [saved, layout]);

    const effective = useMemo(() => {
        if (!layout) return null;
        const blocks = (layout.inherit_blocks !== false ? template?.blocks : layout.blocks) || [];
        return { blocks: blocks.filter((b) => b.visible), extras: layout.extras || [] };
    }, [layout, template]);

    const extras = layout?.extras || [];
    const patchExtras = (rows) => { setLayout((l) => ({ ...l, extras: rows })); setLayoutDirty(true); };
    const patchSection = (id, patch) =>
        patchExtras(extras.map((s) => (s.id === id ? { ...s, ...patch } : s)));

    const move = (id, dir) => {
        const list = [...extras];
        const i = list.findIndex((s) => s.id === id);
        const j = i + dir;
        if (i < 0 || j < 0 || j >= list.length) return;
        [list[i], list[j]] = [list[j], list[i]];
        patchExtras(list);
    };

    const onEdit = (payload) => {
        if (!editable) return toast.error("You do not have permission to edit this product");
        const section = extras.find((s) => s.id === payload.id);
        switch (payload.kind) {
            case "product-text":
                return setInline({ ...payload, target: "product" });
            case "product-images":
                return setImages(true);
            case "section-text":
                return setInline({ ...payload, target: "section" });
            case "add-section":
                return setShowAdd(true);
            case "section-action":
                if (!section) return undefined;
                if (payload.action === "up") return move(payload.id, -1);
                if (payload.action === "down") return move(payload.id, 1);
                if (payload.action === "hide") return patchSection(payload.id, { visible: section.visible === false });
                if (payload.action === "delete") {
                    if (!window.confirm("Delete this section from the product page?")) return undefined;
                    return patchExtras(extras.filter((s) => s.id !== payload.id));
                }
                if (payload.action === "duplicate") {
                    const copy = JSON.parse(JSON.stringify(section));
                    copy.id = `psec_${Date.now()}`;
                    copy.name = `${copy.name || copy.type} copy`;
                    const list = [...extras];
                    list.splice(extras.indexOf(section) + 1, 0, copy);
                    return patchExtras(list);
                }
                if (payload.action === "style") return setStyleFor(payload.id);
                if (payload.action === "preset") return setPresetFor(section);
                if (payload.action === "inspect") return setInspectFor(payload.id);
                return undefined;
            default:
                return undefined;
        }
    };

    const applyInline = (value) => {
        if (inline.target === "product") {
            setDraft((d) => setPath(d, inline.path, value));
            setDirtyRoots((r) => Array.from(new Set([...r, inline.path.split(".")[0]])));
            setProductDirty(true);
        } else {
            const section = extras.find((s) => s.id === inline.id);
            patchSection(inline.id, { props: { ...(section.props || {}), [inline.field]: value } });
        }
        setInline(null);
    };

    const addSection = (type, preset) => {
        const base = preset
            ? { id: `psec_${Date.now()}`, type: preset.section_type, name: preset.name, visible: true,
                props: JSON.parse(JSON.stringify(preset.props || {})) }
            : { ...blankSection(type), id: `psec_${Date.now()}`, visible: true };
        patchExtras([...extras, base]);
        setShowAdd(false);
        setInspectFor(base.id);
    };

    const save = async () => {
        setSaving(true);
        try {
            if (productDirty) {
                const body = {};
                dirtyRoots.forEach((k) => { body[k] = draft[k]; });
                await rhm.patch(`/products/${productId}`, body);
                qc.invalidateQueries({ queryKey: ["rhm-product", productId] });
                qc.invalidateQueries({ queryKey: ["rhm-products"] });
            }
            if (layoutDirty) {
                const { data } = await rhm.put(`/site/product-pages/${productId}`, { extras: layout.extras });
                setLayout((l) => ({ ...l, extras: data.extras }));
                qc.setQueryData(["pl-page", productId], (prev) => ({ ...(prev || {}), ...data }));
            }
            setProductDirty(false);
            setLayoutDirty(false);
            setDirtyRoots([]);
            toast.success("Preview edits saved");
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };

    const discard = () => {
        setDraft(qc.getQueryData(["rhm-product", productId]));
        setLayout(saved);
        setProductDirty(false);
        setLayoutDirty(false);
        setDirtyRoots([]);
    };

    const inspected = extras.find((s) => s.id === inspectFor);
    const styled = extras.find((s) => s.id === styleFor);

    const dialogs = (
        <>
            {inline && <InlineEditor payload={{ ...inline, field: inline.label || inline.field || inline.path }}
                onClose={() => setInline(null)} onSave={applyInline} />}
            {images && (
                <ImagesDialog value={draft.images || []} onClose={() => setImages(false)}
                    onSave={(rows) => {
                        setDraft((d) => ({ ...d, images: rows }));
                        setDirtyRoots((r) => Array.from(new Set([...r, "images"])));
                        setProductDirty(true);
                        setImages(false);
                    }} />
            )}
            {styled && (
                <StyleDialog section={styled} onClose={() => setStyleFor(null)}
                    onChange={(patch) => patchSection(styled.id, { props: { ...(styled.props || {}), ...patch } })} />
            )}
            {inspected && (
                <Shell title="Section settings" onClose={() => setInspectFor(null)} testId="pv-inspector" wide>
                    <Inspector section={inspected} products={products} editable={editable}
                        setProp={(key, value) => patchSection(inspected.id, {
                            props: { ...(inspected.props || {}), [key]: value } })}
                        setSection={(patch) => patchSection(inspected.id, patch)} />
                </Shell>
            )}
            {presetFor && <SaveTemplateDialog section={presetFor} onClose={() => setPresetFor(null)} />}
            {showAdd && <AddSection types={EXTRA_TYPES} onClose={() => setShowAdd(false)}
                onPick={(type) => addSection(type)} onPickTemplate={(t) => addSection(null, t)} />}
        </>
    );

    return { layout: effective, onEdit, dialogs, save, discard, saving,
        dirty: productDirty || layoutDirty };
};

const ImagesDialog = ({ value, onClose, onSave }) => {
    const [rows, setRows] = useState(value.length ? value : [""]);
    return (
        <Shell title="Product images" onClose={onClose} testId="pv-images-dialog" wide>
            <div className="space-y-4">
                {rows.map((url, i) => (
                    <div key={i} className="border border-white/10 p-3" data-testid={`pv-image-${i}`}>
                        <ImageField field={{ key: `image-${i}`, label: `Image ${i + 1}` }} value={url}
                            onChange={(v) => setRows(rows.map((x, j) => (j === i ? v : x)))} />
                        <Btn variant="ghost" data-testid={`pv-image-remove-${i}`}
                            onClick={() => setRows(rows.filter((_, j) => j !== i))}>REMOVE</Btn>
                    </div>
                ))}
                <Btn variant="ghost" onClick={() => setRows([...rows, ""])} data-testid="pv-image-add">ADD IMAGE</Btn>
            </div>
            <div className="mt-5 flex justify-end gap-2">
                <Btn variant="ghost" onClick={onClose}>CANCEL</Btn>
                <Btn data-testid="pv-images-apply" onClick={() => onSave(rows.filter(Boolean))}>APPLY</Btn>
            </div>
        </Shell>
    );
};

export const StyleDialog = ({ section, onClose, onChange }) => {    const p = section.props || {};
    return (
        <Shell title="Section colour" onClose={onClose} testId="pv-style-dialog">
            <Field label="Accent colour" hint="Cards, rules and labels in this section follow this colour">
                <Select value={p.accent || "gold"} data-testid="pv-style-accent"
                    onChange={(e) => onChange({ accent: e.target.value })}
                    options={ACCENT_OPTIONS.map((o) => ({ value: o, label: o.toUpperCase() }))} />
            </Field>
            {p.accent === "custom" && (
                <Field label="Custom colour (hex)" className="mt-4">
                    <Input value={p.accent_hex || ""} placeholder="#E5A900" data-testid="pv-style-hex"
                        onChange={(e) => onChange({ accent_hex: e.target.value })} />
                </Field>
            )}
            <div className="mt-5 flex justify-end">
                <Btn onClick={onClose} data-testid="pv-style-done">DONE</Btn>
            </div>
        </Shell>
    );
};

export default useProductPreviewEdit;
